import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { claimGamePrize } from '../api';

/* ═══════════════════════════════════════════
   GAMES PAGE — /joaca
   Three mini-games; finishing one wins a voucher. The server draws the prize
   (api/games.py) and emails a single-use code, so the page never decides it.
   ═══════════════════════════════════════════ */

const font = "'Space Grotesk', sans-serif";
const mono = "'JetBrains Mono', monospace";

const GAMES = [
  { id: 'ruleta', icon: '🎡', name: 'Ruleta lui Dracula', desc: 'Învârți o dată și vezi ce câștigi.', color: '#ff79c6' },
  { id: 'puzzle', icon: '🧩', name: 'Puzzle-ul castelului', desc: 'Pune la loc piesele castelului.', color: '#bd93f9' },
  { id: 'perechi', icon: '🧄', name: 'Găsește perechile', desc: 'Întoarce cărțile și găsește perechile.', color: '#50fa7b' },
];

// What the wheel shows; the real odds live on the server (5% and 10% often, 26% rarely)
const WHEEL = [5, 10, 5, 15, 10, 5, 26, 10];
const WHEEL_COLORS = ['#bd93f9', '#ff79c6', '#8be9fd', '#50fa7b', '#bd93f9', '#ff79c6', '#f1fa8c', '#8be9fd'];

const WON_KEY = 'dd_game_prize';
const savedPrize = () => {
  try { return JSON.parse(localStorage.getItem(WON_KEY) || 'null'); } catch { return null; }
};

const inputStyle = {
  width: '100%', boxSizing: 'border-box', background: '#0d0e12', border: '1px solid #282a36',
  borderRadius: 8, padding: '12px 14px', color: '#f8f8f2', fontSize: 15, fontFamily: font, outline: 'none',
};

const btn = (color) => ({
  border: 'none', borderRadius: 10, padding: '13px 22px', cursor: 'pointer', fontFamily: font,
  fontSize: 15, fontWeight: 700, background: color, color: '#0d0e12',
});

const Card = ({ children, color = '#1e2030', style }) => (
  <div style={{
    background: '#111218', border: `1px solid ${color}`, borderRadius: 16, padding: 'clamp(18px,4vw,28px)', ...style,
  }}>{children}</div>
);

/* ─── Name + email → prize ──────────────────── */

function PrizeForm({ game, onPrize, title, button }) {
  const [form, setForm] = useState({ name: '', email: '' });
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      onPrize(await claimGamePrize({ game, ...form }));
    } catch (err) {
      const d = err.response?.data;
      setError(d?.error || d?.email?.[0] || d?.name?.[0] || 'Nu am putut trimite. Încearcă din nou peste un minut.');
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
      {title && <div style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: '#f8f8f2' }}>{title}</div>}
      <input style={inputStyle} required placeholder="Numele tău" value={form.name} autoComplete="name"
        onChange={(e) => setForm({ ...form, name: e.target.value })} />
      <input style={inputStyle} required type="email" placeholder="Emailul tău" value={form.email} autoComplete="email"
        onChange={(e) => setForm({ ...form, email: e.target.value })} />
      {error && <div style={{ color: '#ff5555', fontSize: 13, fontFamily: font }}>{error}</div>}
      <button type="submit" disabled={sending} style={{ ...btn('#50fa7b'), opacity: sending ? 0.6 : 1 }}>
        {sending ? 'Se trimite…' : button}
      </button>
      <p style={{ color: '#6272a4', fontSize: 12, margin: 0, fontFamily: font, lineHeight: 1.5 }}>
        Îți trimitem voucherul pe email. Un singur premiu pe email. Vezi <a href="#regulament" style={{ color: '#8be9fd' }}>regulamentul</a>.
      </p>
    </form>
  );
}

function PrizeResult({ prize }) {
  const [copied, setCopied] = useState(false);
  const until = new Date(prize.valid_until).toLocaleDateString('ro-RO');
  const copy = () => {
    navigator.clipboard?.writeText(prize.code).then(() => setCopied(true), () => {});
  };
  return (
    <div style={{ textAlign: 'center', animation: 'ddPop .5s ease' }}>
      <div style={{ fontSize: 54 }}>🎉</div>
      <div style={{ fontFamily: font, color: '#a0a4b8', fontSize: 15 }}>Ai câștigat</div>
      <div style={{
        fontFamily: font, fontSize: 'clamp(54px,14vw,80px)', fontWeight: 800, lineHeight: 1.05,
        background: 'linear-gradient(135deg,#50fa7b,#8be9fd)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      }}>-{prize.percent}%</div>
      <div style={{ fontFamily: font, color: '#a0a4b8', fontSize: 14, marginBottom: 18 }}>la orice pachet DraculaDev</div>
      <button type="button" onClick={copy} title="Copiază codul" style={{
        fontFamily: mono, fontSize: 'clamp(18px,5vw,24px)', fontWeight: 700, color: '#ff79c6', letterSpacing: 2,
        background: '#13141a', border: '1px dashed #ff79c6', borderRadius: 12, padding: '14px 20px', cursor: 'pointer',
      }}>{prize.code} <span style={{ fontSize: 13, color: '#6272a4' }}>{copied ? '✓ copiat' : '⧉'}</span></button>
      <div style={{ color: '#6272a4', fontSize: 12, fontFamily: font, margin: '10px 0 22px' }}>
        Valabil o singură dată, până la {until}. Ți l-am trimis și pe email.
      </div>
      <Link to={`/comanda?cod=${encodeURIComponent(prize.code)}`} style={{ ...btn('#50fa7b'), textDecoration: 'none', display: 'inline-block' }}>
        Folosește codul →
      </Link>
    </div>
  );
}

/* ─── 🎡 Wheel ──────────────────────────────── */

// The page shows the prize only after the wheel stops (onPrize fires then)
function Wheel({ onPrize }) {
  const [angle, setAngle] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const seg = 360 / WHEEL.length;

  const spin = (prize) => {
    // Land the pointer (top) on a random segment showing the won percent
    const options = WHEEL.map((p, i) => (p === prize.percent ? i : -1)).filter((i) => i >= 0);
    const i = options[Math.floor(Math.random() * options.length)];
    const jitter = (Math.random() - 0.5) * seg * 0.6;
    const target = 360 - (i * seg + seg / 2) + jitter;
    setSpinning(true);
    setAngle((a) => a - (a % 360) + 360 * 6 + target);
    setTimeout(() => onPrize(prize), 5400);
  };

  const r = 150, c = 160;
  const paths = WHEEL.map((p, i) => {
    const a0 = (i * seg - 90) * Math.PI / 180, a1 = ((i + 1) * seg - 90) * Math.PI / 180;
    const mid = ((i + 0.5) * seg - 90) * Math.PI / 180;
    return (
      <g key={i}>
        <path d={`M${c},${c} L${c + r * Math.cos(a0)},${c + r * Math.sin(a0)} A${r},${r} 0 0 1 ${c + r * Math.cos(a1)},${c + r * Math.sin(a1)} Z`}
          fill={WHEEL_COLORS[i]} stroke="#0d0e12" strokeWidth="3" />
        <text x={c + r * 0.66 * Math.cos(mid)} y={c + r * 0.66 * Math.sin(mid)} fill="#0d0e12"
          fontFamily="Space Grotesk, sans-serif" fontWeight="800" fontSize={p === 26 ? 26 : 22}
          textAnchor="middle" dominantBaseline="central"
          transform={`rotate(${(i + 0.5) * seg}, ${c + r * 0.66 * Math.cos(mid)}, ${c + r * 0.66 * Math.sin(mid)})`}>
          {p}%
        </text>
      </g>
    );
  });

  return (
    <div style={{ display: 'grid', gap: 24, justifyItems: 'center' }}>
      <div style={{ position: 'relative', width: '100%', maxWidth: 340, aspectRatio: '1' }}>
        <div style={{
          position: 'absolute', top: -6, left: '50%', transform: 'translateX(-50%)', zIndex: 2,
          width: 0, height: 0, borderLeft: '16px solid transparent', borderRight: '16px solid transparent',
          borderTop: '30px solid #f8f8f2', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,.6))',
        }} />
        <svg viewBox="0 0 320 320" style={{
          width: '100%', height: '100%', transform: `rotate(${angle}deg)`,
          transition: spinning ? 'transform 5s cubic-bezier(.12,.75,.12,1)' : 'none',
          filter: 'drop-shadow(0 0 30px rgba(189,147,249,.35))',
        }}>
          <circle cx={c} cy={c} r={r + 6} fill="#16181e" stroke="#bd93f9" strokeWidth="2" />
          {paths}
          <circle cx={c} cy={c} r="30" fill="#0d0e12" stroke="#bd93f9" strokeWidth="2" />
          <text x={c} y={c} fontSize="30" textAnchor="middle" dominantBaseline="central">🧛</text>
        </svg>
      </div>
      {!spinning && (
        <div style={{ width: '100%', maxWidth: 380 }}>
          <PrizeForm game="ruleta" button="🎡 Învârte ruleta" onPrize={spin}
            title="Scrie-ți numele și emailul, apoi învârte" />
        </div>
      )}
      {spinning && <div style={{ fontFamily: font, color: '#bd93f9', fontSize: 16 }}>Se învârte… 🦇</div>}
    </div>
  );
}

/* ─── 🧩 Sliding puzzle ─────────────────────── */

const N = 3;
const SOLVED = [...Array(N * N).keys()]; // tile k belongs at k; N*N-1 is the empty slot

const neighbours = (pos) => {
  const r = Math.floor(pos / N), c = pos % N;
  return [[r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1]]
    .filter(([y, x]) => y >= 0 && y < N && x >= 0 && x < N).map(([y, x]) => y * N + x);
};

// Shuffle by random legal moves from the solved board, so it is always solvable
function shuffled() {
  const board = [...SOLVED];
  let empty = N * N - 1, prev = -1;
  for (let i = 0; i < 120; i++) {
    const opts = neighbours(empty).filter((p) => p !== prev);
    const next = opts[Math.floor(Math.random() * opts.length)];
    [board[empty], board[next]] = [board[next], board[empty]];
    prev = empty; empty = next;
  }
  return board.every((t, i) => t === i) ? shuffled() : board;
}

function Puzzle({ onSolved }) {
  const [board, setBoard] = useState(shuffled);
  const [moves, setMoves] = useState(0);
  const solved = board.every((t, i) => t === i);

  useEffect(() => { if (solved) onSolved(); }, [solved, onSolved]);

  const move = (pos) => {
    if (solved) return;
    const empty = board.indexOf(N * N - 1);
    if (!neighbours(empty).includes(pos)) return;
    const b = [...board];
    [b[empty], b[pos]] = [b[pos], b[empty]];
    setBoard(b);
    setMoves((m) => m + 1);
  };

  return (
    <div style={{ display: 'grid', gap: 16, justifyItems: 'center' }}>
      <div style={{
        position: 'relative', width: '100%', maxWidth: 330, aspectRatio: '3 / 4', background: '#0d0e12',
        borderRadius: 12, border: '2px solid #bd93f950', overflow: 'hidden', touchAction: 'manipulation',
      }}>
        {board.map((tile, pos) => {
          if (tile === N * N - 1 && !solved) return null;
          const tr = Math.floor(tile / N), tc = tile % N, pr = Math.floor(pos / N), pc = pos % N;
          return (
            <button key={tile} type="button" onClick={() => move(pos)} aria-label={`Piesa ${tile + 1}`} style={{
              position: 'absolute', width: `${100 / N}%`, height: `${100 / N}%`,
              left: `${(pc * 100) / N}%`, top: `${(pr * 100) / N}%`, transition: 'left .18s, top .18s',
              backgroundImage: 'url(/bg-castle.jpg)', backgroundSize: `${N * 100}% ${N * 100}%`,
              backgroundPosition: `${(tc * 100) / (N - 1)}% ${(tr * 100) / (N - 1)}%`,
              border: solved ? 'none' : '1px solid #0d0e12', borderRadius: solved ? 0 : 6, padding: 0, cursor: 'pointer',
              boxShadow: solved ? 'none' : 'inset 0 0 0 1px rgba(189,147,249,.25)',
            }} />
          );
        })}
      </div>
      <div style={{ fontFamily: mono, fontSize: 13, color: '#6272a4' }}>
        {solved ? `✓ Gata în ${moves} mutări!` : `Mutări: ${moves} · atinge o piesă de lângă locul gol`}
      </div>
      {!solved && (
        <button type="button" onClick={() => { setBoard(shuffled()); setMoves(0); }}
          style={{ ...btn('transparent'), color: '#bd93f9', border: '1px solid #bd93f950', fontSize: 13, padding: '8px 16px' }}>
          ↻ Amestecă din nou
        </button>
      )}
    </div>
  );
}

/* ─── 🧄 Memory ─────────────────────────────── */

const SYMBOLS = ['🧄', '🦇', '🏰', '🧛', '🌙', '💻'];
const deal = () => [...SYMBOLS, ...SYMBOLS]
  .map((s) => ({ s, k: Math.random() })).sort((a, b) => a.k - b.k).map(({ s }, i) => ({ id: i, s }));

function Memory({ onSolved }) {
  const [cards] = useState(deal);
  const [open, setOpen] = useState([]);
  const [found, setFound] = useState([]);
  const [tries, setTries] = useState(0);
  const busy = useRef(false);
  const solved = found.length === cards.length;

  useEffect(() => { if (solved) onSolved(); }, [solved, onSolved]);

  const flip = (card) => {
    if (busy.current || open.includes(card.id) || found.includes(card.id)) return;
    const next = [...open, card.id];
    setOpen(next);
    if (next.length < 2) return;
    setTries((t) => t + 1);
    const [a, b] = next.map((id) => cards[id]);
    busy.current = true;
    setTimeout(() => {
      if (a.s === b.s) setFound((f) => [...f, a.id, b.id]);
      setOpen([]);
      busy.current = false;
    }, a.s === b.s ? 350 : 800);
  };

  return (
    <div style={{ display: 'grid', gap: 16, justifyItems: 'center' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'clamp(6px,2vw,10px)', width: '100%', maxWidth: 380 }}>
        {cards.map((card) => {
          const up = open.includes(card.id) || found.includes(card.id);
          return (
            <button key={card.id} type="button" onClick={() => flip(card)} aria-label={up ? card.s : 'Carte întoarsă'} style={{
              aspectRatio: '3 / 4', borderRadius: 10, cursor: 'pointer', padding: 0,
              fontSize: 'clamp(26px,8vw,38px)', transition: 'transform .25s, background .25s',
              transform: up ? 'rotateY(0)' : 'rotateY(180deg)',
              background: up ? (found.includes(card.id) ? '#50fa7b20' : '#1a1c28') : 'linear-gradient(135deg,#bd93f9,#ff79c6)',
              border: `1px solid ${found.includes(card.id) ? '#50fa7b80' : '#282a36'}`,
            }}>
              <span style={{ display: 'inline-block', transform: up ? 'none' : 'rotateY(180deg)' }}>{up ? card.s : '🦇'}</span>
            </button>
          );
        })}
      </div>
      <div style={{ fontFamily: mono, fontSize: 13, color: '#6272a4' }}>
        {solved ? `✓ Toate perechile în ${tries} încercări!` : `Încercări: ${tries} · perechi găsite: ${found.length / 2} din ${SYMBOLS.length}`}
      </div>
    </div>
  );
}

/* ─── Page ──────────────────────────────────── */

function GameBox({ game, onPrize }) {
  const [done, setDone] = useState(false);
  const finish = useMemo(() => () => setDone(true), []);
  if (game.id === 'ruleta') return <Wheel onPrize={onPrize} />;
  const Board = game.id === 'puzzle' ? Puzzle : Memory;
  return (
    <div style={{ display: 'grid', gap: 22, justifyItems: 'center' }}>
      <Board onSolved={finish} />
      {done && (
        <div style={{ width: '100%', maxWidth: 380, animation: 'ddPop .4s ease' }}>
          <PrizeForm game={game.id} onPrize={onPrize} button="🎁 Vreau premiul" title="Bravo! Unde îți trimitem voucherul?" />
        </div>
      )}
    </div>
  );
}

const RULES = [
  'Jocurile de pe draculadev.com/joaca sunt organizate de DraculaDev și sunt gratuite.',
  'Poate participa orice persoană de peste 18 ani, cu un nume și o adresă de email reale.',
  'Premiul este un voucher de reducere de 5%, 10%, 15% sau 26% la orice pachet DraculaDev. Premiul se alege automat, la întâmplare: 5% și 10% ies cel mai des, 26% foarte rar.',
  'Se acordă un singur voucher pentru fiecare adresă de email, indiferent de joc.',
  'Voucherul este valabil 30 de zile, pentru o singură comandă, și nu se poate transforma în bani sau combina cu alt cod.',
  'Numele și emailul le folosim ca să-ți trimitem voucherul și, ocazional, ca să te contactăm despre oferta noastră. Poți cere oricând ștergerea datelor la contact@draculadev.com.',
  'DraculaDev poate opri sau modifica jocurile oricând. Voucherele deja câștigate rămân valabile până la data de expirare.',
];

const GamesPage = () => {
  const [active, setActive] = useState('ruleta');
  const [prize, setPrize] = useState(savedPrize);
  const game = GAMES.find((g) => g.id === active);

  const onPrize = (p) => {
    setPrize(p);
    try { localStorage.setItem(WON_KEY, JSON.stringify(p)); } catch { /* storage unavailable */ }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0b0f', color: '#f8f8f2', fontFamily: font }}>
      <style>{`@keyframes ddPop{from{opacity:0;transform:scale(.92)}to{opacity:1;transform:none}}`}</style>
      <nav style={{
        position: 'sticky', top: 0, zIndex: 50, background: 'rgba(10,11,15,0.85)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid #1a1c28', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <Link to="/" style={{ color: '#f8f8f2', textDecoration: 'none', fontWeight: 700, fontSize: 18 }}>
          🧛 Dracula<span style={{ color: '#bd93f9' }}>Dev</span>
        </Link>
        <Link to="/comanda" style={{ color: '#50fa7b', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>Comandă un site →</Link>
      </nav>

      <main style={{ maxWidth: 760, margin: '0 auto', padding: 'clamp(28px,6vw,56px) 16px 60px' }}>
        <header style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            display: 'inline-block', background: '#ff79c612', border: '1px solid #ff79c630', borderRadius: 20,
            padding: '5px 14px', fontFamily: mono, fontSize: 12, color: '#ff79c6', marginBottom: 16,
          }}>🎁 vouchere de până la 26%</div>
          <h1 style={{ fontSize: 'clamp(32px,7vw,52px)', margin: 0, lineHeight: 1.1, fontWeight: 800 }}>
            Joacă și <span style={{
              background: 'linear-gradient(135deg,#bd93f9,#ff79c6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>câștigă</span>
          </h1>
          <p style={{ color: '#a0a4b8', fontSize: 16, maxWidth: 520, margin: '14px auto 0', lineHeight: 1.6 }}>
            Alege un joc, termină-l și primești un voucher de reducere la orice pachet DraculaDev: site de prezentare, magazin online sau aplicație.
          </p>
        </header>

        {prize ? (
          <Card color="#50fa7b40"><PrizeResult prize={prize} /></Card>
        ) : (
          <>
            <div role="tablist" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
              {GAMES.map((g) => (
                <button key={g.id} type="button" role="tab" aria-selected={active === g.id} onClick={() => setActive(g.id)} style={{
                  background: active === g.id ? `${g.color}18` : '#111218', border: `1px solid ${active === g.id ? g.color : '#1e2030'}`,
                  borderRadius: 12, padding: '14px 8px', cursor: 'pointer', color: '#f8f8f2', fontFamily: font, textAlign: 'center',
                }}>
                  <div style={{ fontSize: 28 }}>{g.icon}</div>
                  <div style={{ fontSize: 'clamp(12px,3.2vw,14px)', fontWeight: 700, marginTop: 4, color: active === g.id ? g.color : '#f8f8f2' }}>{g.name}</div>
                </button>
              ))}
            </div>
            <Card color={`${game.color}40`}>
              <p style={{ textAlign: 'center', color: '#a0a4b8', margin: '0 0 22px', fontSize: 15 }}>{game.desc}</p>
              <GameBox key={game.id} game={game} onPrize={onPrize} />
            </Card>
          </>
        )}

        <details id="regulament" style={{ marginTop: 40, color: '#8b8fa3', fontSize: 13, lineHeight: 1.6 }}>
          <summary style={{ cursor: 'pointer', color: '#f8f8f2', fontWeight: 600, fontSize: 14 }}>Regulamentul jocurilor</summary>
          <ol style={{ paddingLeft: 20, marginTop: 12 }}>
            {RULES.map((r) => <li key={r} style={{ marginBottom: 6 }}>{r}</li>)}
          </ol>
        </details>
      </main>
    </div>
  );
};

export default GamesPage;
